import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, ArrowLeft, Home, Search } from 'lucide-react';
import { Link, isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';
import { Layout } from '@/components/layout/Layout';

export const ErrorPage: React.FC = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  const is404 = isRouteErrorResponse(error) && error.status === 404;
  const is403 = isRouteErrorResponse(error) && error.status === 403;

  const getErrorDetails = () => {
    if (is404) {
      return {
        title: 'Seite nicht gefunden',
        description:
          'Die von Ihnen angeforderte Seite existiert nicht oder wurde verschoben. Bitte überprüfen Sie die URL oder nutzen Sie die Navigation.',
        statusCode: '404',
      };
    }
    if (is403) {
      return {
        title: 'Zugriff verweigert',
        description:
          'Sie haben keine Berechtigung, auf diese Seite zuzugreifen. Bitte melden Sie sich mit einem autorisierten Konto an.',
        statusCode: '403',
      };
    }
    return {
      title: 'Ein Fehler ist aufgetreten',
      description:
        'Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es später erneut oder kehren Sie zur Startseite zurück.',
      statusCode: isRouteErrorResponse(error) ? String(error.status) : 'Fehler',
    };
  };

  const { title, description, statusCode } = getErrorDetails();

  return (
    <Layout>
      <section className='flex flex-col items-center justify-center gap-8 py-16 text-center sm:py-20'>
        <Card className='w-full max-w-2xl border-neutral-hellgrau shadow-sm'>
          <CardHeader className='space-y-6'>
            <div className='mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-swd-hellblau text-swd-tuerkis-darker'>
              <AlertCircle className='h-10 w-10' aria-hidden='true' />
            </div>
            <div className='space-y-2'>
              <p className='text-5xl font-bold text-swd-tuerkis'>{statusCode}</p>
              <CardTitle className='text-2xl font-semibold tracking-tight text-swd-tuerkis-darker sm:text-3xl'>
                {title}
              </CardTitle>
            </div>
            <CardDescription className='text-base leading-6 text-neutral-grau'>{description}</CardDescription>
          </CardHeader>
          <CardContent className='flex flex-col items-center gap-6'>
            <div className='flex w-full flex-col gap-3 sm:flex-row sm:justify-center'>
              <Button variant='outline' onClick={() => navigate(-1)} className='w-full sm:w-auto'>
                <ArrowLeft className='mr-2 h-4 w-4' aria-hidden='true' /> Zurück
              </Button>
              <Button asChild className='w-full sm:w-auto'>
                <Link to='/'>
                  <Home className='mr-2 h-4 w-4' aria-hidden='true' /> Zur Startseite
                </Link>
              </Button>
            </div>
            <div className='space-y-2 text-left text-sm text-muted-foreground'>
              <p className='font-medium text-neutral-dunkelgrau'>Schnelle Navigation</p>
              <div className='grid gap-2 sm:grid-cols-2'>
                <Button asChild variant='ghost' className='justify-start text-foreground'>
                  <Link to='/coupons'>
                    <Search className='mr-2 h-4 w-4' aria-hidden='true' />
                    Gutscheine entdecken
                  </Link>
                </Button>
                <Button asChild variant='ghost' className='justify-start text-foreground'>
                  <Link to='/meine-gutscheine'>Meine Gutscheine</Link>
                </Button>
                <Button asChild variant='ghost' className='justify-start text-foreground'>
                  <Link to='/so-funktioniert-es'>So funktioniert es</Link>
                </Button>
                <Button asChild variant='ghost' className='justify-start text-foreground'>
                  <Link to='/balance'>Mein Guthaben</Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
    </Layout>
  );
};
